import { getFunctions, httpsCallable } from "firebase/functions";
import { useCallback, useState } from "react";
import { Button } from "react-bootstrap";
import toast from "react-hot-toast";
import Select, { SingleValue } from "react-select";
import { TwoRowSelectOption } from "../../components/Form/TwoRowSelectOption";
import { TwoRowSelectSingleValue } from "../../components/Form/TwoRowSelectSingleValue";
import { toastOnError } from "../../lib/toast-on-error";
import "./FirebaseFunctionsRunner.scss";

type RunnableFunction = {
  name: string;
  description: string;
};

const RunnableFunctions: RunnableFunction[] = [
  {
    name: "processUnmappedImportedInvestorEntries",
    description:
      "Triggers processing of all *unmapped* imported investor entries",
  },
  {
    name: "processAllImportedInvestorEntries",
    description: "Triggers processing of all imported investor entries",
  },
  {
    name: "triggerAggregationNow",
    description: "Triggers aggregation of stats, stage and sector focuses",
  },
];

interface FirebaseFunctionsRunnerProps {}

const FirebaseFunctionsRunner: React.FC<FirebaseFunctionsRunnerProps> = () => {
  const [running, setRunning] = useState(false);
  const [selectedFunction, setSelectedFunction] =
    useState<RunnableFunction | null>(null);

  const handleRunFunction = useCallback(async () => {
    if (running) {
      toast.error("Function is already running");
      return;
    }

    if (!selectedFunction) {
      toast.error("Please select a function");
      return;
    }

    setRunning(true);
    try {
      const functions = getFunctions();
      const yourFunction = httpsCallable(functions, selectedFunction.name);
      const result = await yourFunction({ data: "yup" });
      console.log(result);
      toast.success(
        "Function run successfully? Check the console for details.",
      );
      setSelectedFunction(null);
    } catch (err) {
      toastOnError(err, `Error running function ${selectedFunction.name}`);
    } finally {
      setRunning(false);
    }
  }, [running, selectedFunction]);

  const handleFunctionChange = useCallback(
    (option: SingleValue<{ name: string; description: string }>) => {
      if (option) {
        const singleOption = option as { name: string; description: string };
        setSelectedFunction({
          name: singleOption.name,
          description: singleOption.description,
        });
      } else {
        setSelectedFunction(null);
      }
    },
    [],
  );

  return (
    <div className="firebase-functions-runner">
      <div className="form-header">Firebase Functions Runner</div>
      <div className="form-group">
        <label>Function Name</label>
        <Select
          className="react-select"
          classNamePrefix="function-runner-select"
          options={RunnableFunctions}
          name="functionName"
          value={selectedFunction ?? null}
          onChange={handleFunctionChange}
          components={{
            Option: TwoRowSelectOption,
            SingleValue: TwoRowSelectSingleValue,
          }}
          placeholder="Select a function..."
          isMulti={false}
          isSearchable={false}
          isClearable
        />
        <Button
          variant="primary"
          onClick={handleRunFunction}
          disabled={!selectedFunction || running}
        >
          {running ? "Running..." : "Run"}
        </Button>
      </div>
    </div>
  );
};

export default FirebaseFunctionsRunner;
